"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight, Home } from "lucide-react";
import { cn } from "@/lib/utils";
import { create } from "zustand";

interface BreadcrumbStore {
  labels: Record<string, string>;
  setLabel: (id: string, label: string) => void;
}

export const useBreadcrumbStore = create<BreadcrumbStore>((set) => ({
  labels: {},
  setLabel: (id, label) =>
    set((state) => ({
      labels: { ...state.labels, [id]: label },
    })),
}));

const routeNameMap: Record<string, string> = {
  strategy: "Strategy",
  goals: "Goals",
  swot: "Company Health",
  risks: "Risks",
  decisions: "Decisions",
  sandbox: "Simulation Sandbox",
  "daily-pulse": "Daily Pulse",
  "war-room": "War Room",
  work: "Work",
  projects: "Projects",
  tasks: "Tasks & Sprints",
  timesheets: "Timesheets",
  documents: "Documents",
  sales: "Sales",
  leads: "Leads",
  clients: "Clients",
  proposals: "Proposals & Contracts",
  finance: "Finance",
  overview: "Overview",
  invoices: "Invoices",
  costs: "Costs & Expenses",
  people: "People",
  teams: "Teams",
  capacity: "Capacity & Workloads",
  "time-off": "Time Off",
  kudos: "Growth & Kudos",
  burnout: "Burnout Heatmeter",
  operations: "Operations",
  suppliers: "Suppliers",
  assets: "Assets & Inventory",
  initiatives: "Initiatives",
  ledger: "Semantic Ledger",
  explore: "Explore",
  settings: "Settings",
  profile: "Profile",
  sharing: "Sharing",
};

export function Breadcrumb() {
  const pathname = usePathname();
  const labels = useBreadcrumbStore((state) => state.labels);

  if (pathname === "/") {
    return (
      <div className="flex items-center text-sm font-medium text-text-secondary">
        <Home className="h-4 w-4 mr-1 text-text-muted" />
        <span>Overview</span>
      </div>
    );
  }

  const pathSegments = pathname.split("/").filter(Boolean);

  return (
    <nav className="flex" aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-2">
        <li className="inline-flex items-center">
          <Link
            href="/"
            className="inline-flex items-center text-sm font-medium text-text-secondary hover:text-primary-600 transition-colors"
          >
            <Home className="h-4 w-4 mr-1 text-text-muted" />
            Home
          </Link>
        </li>
        {pathSegments.map((segment, idx) => {
          const href = `/${pathSegments.slice(0, idx + 1).join("/")}`;
          const isLast = idx === pathSegments.length - 1;
          let label = labels[segment] || routeNameMap[segment];
          if (!label) {
            label = segment
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());
          }

          return (
            <li key={href} className="inline-flex items-center">
              <ChevronRight className="h-4 w-4 text-text-muted mx-1 shrink-0" />
              {isLast ? (
                <span className="text-sm font-semibold text-text-primary select-none">
                  {label}
                </span>
              ) : (
                <Link
                  href={href}
                  className="text-sm font-medium text-text-secondary hover:text-primary-600 transition-colors"
                >
                  {label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
